import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { darcula } from "react-syntax-highlighter/dist/esm/styles/prism";
import SEO from "../components/SEO";
import matter from "gray-matter";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  FaRegCopy,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";

const blogFiles = {
  "java-tips": "java-tips.md",
  "string-comparision": "string-comparision.md",
  "hashset-duplicate": "hashset-duplicate.md",
  "netlify-deploy": "netlify-deploy.md",
  "react-joyride": "react-joyride.md",
};

const BlogPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [allBlogs, setAllBlogs] = useState([]);

  // Load current blog
  useEffect(() => {
    const loadBlog = async () => {
      if (!blogFiles[slug]) return;
      const res = await import(`../blogs/${blogFiles[slug]}`);
      const text = await fetch(res.default).then((r) => r.text());
      const { data, content } = matter(text);
      setBlog({
        slug,
        title: data.title || slug,
        content,
        author: data.author || "CodeRocker",
        date: data.date || new Date().toLocaleDateString(),
        tags: data.tags || ["others"],
      });
    };
    loadBlog();
  }, [slug]);

  // Load all blogs metadata
  useEffect(() => {
    const loadAllBlogs = async () => {
      const blogs = await Promise.all(
        Object.entries(blogFiles).map(async ([slug, file]) => {
          const res = await import(`../blogs/${file}`);
          const text = await fetch(res.default).then((r) => r.text());
          const { data } = matter(text);
          return {
            slug,
            title: data.title || slug,
            tags: data.tags || ["others"],
          };
        })
      );
      setAllBlogs(blogs);
    };
    loadAllBlogs();
  }, []);

  if (!blog)
    return (
      <p className="text-center py-10 text-gray-500 dark:text-gray-400">
        Loading blog...
      </p>
    );

  // Compute related articles based on tag intersection
  const relatedArticles = allBlogs.filter(
    (a) => a.slug !== slug && a.tags.some((tag) => blog.tags.includes(tag))
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 font-sans text-gray-900 dark:text-gray-100">
      <SEO
        title={blog.title}
        description={blog.content.substring(0, 150)}
        keywords={blog.tags.join(", ")}
        url={`https://coderocker.netlify.app/blogs/${slug}`}
      />

      {/* Hero */}
      <div className="rounded-xl shadow-lg p-8 mb-12 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 transition-colors">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-4 text-indigo-700 dark:text-indigo-400 leading-tight">
          {blog.title}
        </h1>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-gray-700 dark:text-gray-300">
          <p className="font-medium text-lg">By {blog.author}</p>
          <p className="text-sm mt-2 sm:mt-0">
            {new Date(blog.date).toLocaleDateString()}
          </p>
        </div>
      </div>

      <div className="lg:flex lg:space-x-8">
        {/* Main Content */}
        <div className="flex-1">
          <ReactMarkdown
            children={blog.content}
            components={{
              h1: ({ children, ...props }) => (
                <h1
                  className="text-3xl sm:text-4xl mt-10 mb-6 font-bold border-b pb-2 border-gray-200 dark:border-gray-700"
                  {...props}
                >
                  {children}
                </h1>
              ),
              h2: ({ children, ...props }) => (
                <h2
                  className="text-2xl sm:text-3xl mt-8 mb-4 font-semibold text-indigo-600 dark:text-indigo-400"
                  {...props}
                >
                  {children}
                </h2>
              ),
              h3: ({ children, ...props }) => (
                <h3
                  className="text-xl sm:text-2xl mt-6 mb-3 font-semibold text-gray-800 dark:text-gray-200"
                  {...props}
                >
                  {children}
                </h3>
              ),
              p: ({ children, ...props }) => (
                <p className="mb-6 leading-relaxed text-lg" {...props}>
                  {children}
                </p>
              ),
              li: ({ children, ...props }) => (
                <li className="ml-6 list-disc mb-3 text-lg" {...props}>
                  {children}
                </li>
              ),
              // ✅ Custom link override
              a: ({ href, children, ...props }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200"
                  {...props}
                >
                  {children}
                </a>
              ),
              code({ inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || "");
                if (!inline && match) {
                  return (
                    <div className="relative my-6 rounded-lg overflow-hidden shadow-lg bg-gray-900">
                      <CopyToClipboard
                        text={String(children)}
                        onCopy={() => {
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                      >
                        <button className="absolute right-2 top-2 bg-indigo-600 text-white text-xs px-2 py-1 rounded hover:bg-indigo-500 flex items-center gap-1 z-10">
                          {copied ? <FaCheck /> : <FaRegCopy />}{" "}
                          {copied ? "Copied" : "Copy"}
                        </button>
                      </CopyToClipboard>
                      <SyntaxHighlighter
                        style={darcula}
                        language={match[1]}
                        PreTag="div"
                        className="rounded-b-lg"
                        {...props}
                      >
                        {String(children).replace(/\n$/, "")}
                      </SyntaxHighlighter>
                    </div>
                  );
                }
                return (
                  <code
                    className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded"
                    {...props}
                  >
                    {children}
                  </code>
                );
              },
              img: ({ ...props }) => (
                <img
                  className="my-8 rounded-lg shadow-md w-full"
                  {...props}
                  alt={props.alt}
                />
              ),
              blockquote: ({ children, ...props }) => (
                <blockquote className="border-l-4 border-indigo-600 pl-6 italic text-gray-700 dark:text-gray-300 my-6 bg-indigo-50 dark:bg-gray-700 rounded-md">
                  {children}
                </blockquote>
              ),
            }}
          />

          {/* Previous / Next */}
          {relatedArticles.length > 0 && (
            <div className="flex justify-between mt-12 border-t pt-6 border-gray-200 dark:border-gray-700">
              {relatedArticles[0] && (
                <Link
                  to={`/blogs/${relatedArticles[0].slug}`}
                  className="flex items-center text-indigo-600 dark:text-indigo-300 hover:underline"
                >
                  <FaArrowLeft className="mr-2" /> {relatedArticles[0].title}
                </Link>
              )}
              {relatedArticles[1] && (
                <Link
                  to={`/blogs/${relatedArticles[1].slug}`}
                  className="flex items-center text-indigo-600 dark:text-indigo-300 hover:underline ml-auto"
                >
                  {relatedArticles[1].title} <FaArrowRight className="ml-2" />
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        {relatedArticles.length > 0 && (
          <aside className="w-full lg:w-64 mt-10 lg:mt-0">
            {/* Mobile toggle */}
            <div className="lg:hidden mb-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="w-full bg-indigo-600 dark:bg-indigo-500 text-white px-4 py-2 rounded flex justify-between items-center"
              >
                Read Next
                {sidebarOpen ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>

            <div
              className={`lg:sticky lg:top-20 p-4 bg-indigo-50 dark:bg-gray-800 rounded-lg shadow-md transition-all duration-300 ${
                sidebarOpen ? "block" : "hidden lg:block"
              }`}
            >
              <h3 className="text-lg font-semibold mb-4 text-indigo-700 dark:text-indigo-400">
                Read Next
              </h3>
              <ul className="space-y-2">
                {relatedArticles.map((article) => (
                  <li key={article.slug}>
                    <Link
                      to={`/blogs/${article.slug}`}
                      className="text-indigo-600 dark:text-indigo-300 hover:underline"
                    >
                      {article.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
